import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Multi-select for data tables (Ctrl/Cmd+click toggle, Shift+click range).
 * Plain click is left to the page (open detail / navigate); Escape clears.
 */
export function useRowSelection() {
  const [selected, setSelected] = useState<Set<number>>(() => new Set());
  const lastIdRef = useRef<number | null>(null);

  const clear = useCallback(() => {
    setSelected(new Set());
    lastIdRef.current = null;
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selected.size > 0) clear();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected.size, clear]);

  const isSelected = useCallback((id: number) => selected.has(id), [selected]);

  /**
   * Returns true when the click was consumed for multi-select (caller should
   * not navigate / open a detail modal).
   *
   * @param orderedIds Current visual row order — required for Shift+range.
   */
  const handleRowClick = useCallback(
    (
      e: {
        ctrlKey: boolean;
        metaKey: boolean;
        shiftKey: boolean;
        preventDefault: () => void;
        stopPropagation: () => void;
      },
      id: number,
      orderedIds?: readonly number[],
    ): boolean => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        e.stopPropagation();
        setSelected((prev) => {
          const next = new Set(prev);
          if (next.has(id)) next.delete(id);
          else next.add(id);
          return next;
        });
        lastIdRef.current = id;
        return true;
      }

      if (e.shiftKey && lastIdRef.current != null && orderedIds && orderedIds.length > 0) {
        e.preventDefault();
        e.stopPropagation();
        const a = orderedIds.indexOf(lastIdRef.current);
        const b = orderedIds.indexOf(id);
        if (a >= 0 && b >= 0) {
          const lo = Math.min(a, b);
          const hi = Math.max(a, b);
          setSelected((prev) => {
            const next = new Set(prev);
            for (let i = lo; i <= hi; i++) next.add(orderedIds[i]!);
            return next;
          });
          return true;
        }
      }

      // Plain click: drop multi-select so a normal open/navigate feels clean.
      if (selected.size > 0) {
        setSelected(new Set());
      }
      lastIdRef.current = id;
      return false;
    },
    [selected.size],
  );

  /** Call on mousedown so Ctrl/Shift+click does not start a text selection. */
  const suppressTextSelect = useCallback(
    (e: { ctrlKey: boolean; metaKey: boolean; shiftKey: boolean; preventDefault: () => void }) => {
      if (e.ctrlKey || e.metaKey || e.shiftKey) e.preventDefault();
    },
    [],
  );

  return {
    selected,
    selectedIds: Array.from(selected),
    count: selected.size,
    isSelected,
    clear,
    handleRowClick,
    suppressTextSelect,
  };
}
